import React, { useEffect, useState } from "react";
import TopNavTabs from "./TopNavTabs";
import PERMISSIONS, { apiFetch } from "../utils/wkCommon";
import { isZh } from "../utils/language";
import { getUser } from "../utils/auth";
import {hasUmatPermissionAsync} from "../utils/permissionsAsync";
import {buildInitialAgendaAttributes} from "../utils/agendaBookUtils"

const user = getUser();

export default function AgendaAttributeConfigModal({
  open,
  onClose,
  agendaId,
  agendaBook,
  agenda
}) {
  const [canReadUmatDataFull, setCanReadUmatDataFull] = useState(false);
  const [permsReady, setPermsReady] = useState(false);

  const [activeTab, setActiveTab] = useState("attendance");

  const [attrs, setAttrs] = useState({
    // BASIC
    includePhone: false,
    includeAddress: false,
    includeDutyLevel: false,
    includeArea: false,
    includeFotang: false,
    includeEducation: false,
    includeOccupation: false,

    // 🔵 UMATDATA_FULL
    includeBirthdate: false,         
    includeQiudaoDate: false,
    includeYinshi: false,
    includeBaoshi: false,
    includeFotangQiudao: false,
    includeQingkou: false,
    includeSd3hari: false,
    includeMingde: false,
    includeXinmin: false,
    includeZhishan: false,

    // BASIC
    exportSplitOption: "role_gender"
  });

  const [reg, setReg] = useState({
    allowPreRegistration: false,
    allowUmatExternal: false,
    allowPublic: false,
    FaHui: false,
    oneTimeTagNumber: false
  });

  const [report, setReport] = useState({
    lunarDate: "",
    agendaDate: "",
    areaNameId: "",
    areaNameZh: "",
    agendaDateText: "",
    agendaSubjectId: "",
    agendaSubjectZh: "",
    reportTemplateId: "",
    financeSectionTitleId: "",
    financeSectionTitleZh: "",
    rundownSectionTitleId: "",
    rundownSectionTitleZh: "",
    personnelSectionTitleId: "",
    personnelSectionTitleZh: ""
  });

  const [saving, setSaving] = useState(false);

  /* ================= LOAD PERMISSION ================= */
  useEffect(() => {
    let mounted = true;

    async function loadPerms() {
      try {
        const email = user?.email || user?.LoginEmail;

        const a = await hasUmatPermissionAsync(
          email,
          PERMISSIONS.UMATDATA_FULL
        );
        if (mounted) setCanReadUmatDataFull(Boolean(a));
      } catch (e) {
        console.warn("loadPerms failed", e);
      } finally {
        if (mounted) setPermsReady(true);
      }
    }

    loadPerms();
    return () => {
      mounted = false;
    };
  }, [user]);

  useEffect(() => {
    if (!open) { return; }
    if (!agendaBook?.agendaAttributes) return;
    
    const a = agendaBook?.agendaAttributes || {};

    setAttrs({
      includePhone: !!a.includePhone,
      includeAddress: !!a.includeAddress,
      includeDutyLevel: !!a.includeDutyLevel,
      includeArea: !!a.includeArea,
      includeFotang: !!a.includeFotang, 
      includeEducation: !!a.includeEducation,
      includeOccupation: !!a.includeOccupation,

      includeBirthdate: !!a.includeBirthdate,
      includeQiudaoDate: !!a.includeQiudaoDate,
      includeYinshi: !!a.includeYinshi,
      includeBaoshi: !!a.includeBaoshi,
      includeFotangQiudao: !!a.includeFotangQiudao,
      includeQingkou: !!a.includeQingkou,
      includeSd3hari: !!a.includeSd3hari,
      includeMingde: !!a.includeMingde,
      includeXinmin: !!a.includeXinmin,
      includeZhishan: !!a.includeZhishan,

      exportSplitOption: a.exportSplitOption || "role_gender"
    });

    setReg({
      allowPreRegistration: !!agenda?.allowPreRegistration,
      allowUmatExternal: !!agenda?.allowUmatExternal,
      allowPublic: !!agenda?.allowPublic,
      FaHui: !!agenda?.FaHui,
      oneTimeTagNumber: !!agenda?.oneTimeTagNumber
    });

    setReport({
      lunarDate: a.lunarDate || "",
      agendaDate: a.agendaDate || "",
      areaNameId: a.areaNameId || "",
      areaNameZh: a.areaNameZh || "",
      agendaDateText: a.agendaDateText || "",
      agendaSubjectId: a.agendaSubjectId || "",
      agendaSubjectZh: a.agendaSubjectZh || "",
      reportTemplateId: a.reportTemplateId || "",
      financeSectionTitleId: a.financeSectionTitleId || "",
      financeSectionTitleZh: a.financeSectionTitleZh || "",
      rundownSectionTitleId: a.rundownSectionTitleId || "",
      rundownSectionTitleZh: a.rundownSectionTitleZh || "",
      personnelSectionTitleId: a.personnelSectionTitleId || "",
      personnelSectionTitleZh: a.personnelSectionTitleZh || ""
    });    
  }, [open, agendaBook]);

  /* ================= SAVE ================= */
  async function handleSave() {

    if (saving) return;
    setSaving(true);

    try {

      if (agendaBook?.documentId) {
        await apiFetch(`/api/agenda-books/${agendaBook.documentId}`, {
          method: "PUT",
          body: {
            data: {
              agendaAttributes: {
                ...(agendaBook?.agendaAttributes || {}),
                ...attrs,
                ...report
              }
            }
          }
        });
      }

      if (agendaId) {
        await apiFetch(`/api/agendas/${agendaId}`, {
          method: "PUT",
          body: {
            data: {
              ...reg
            }
          }
        });
      }

      onClose(true);

    } catch (e) {
      console.error(e);
      alert("Save failed");
    } finally {
      setSaving(false);
    }
  }

  /* ================= HELPER ================= */
  async function handleRebuildAttributes(){

    if(!agendaBook?.documentId) return;

    if(!window.confirm("Rebuild attributes dari data agenda terbaru?")) return;

    try{
      const systemFields = [
        "areaNameZh",
        "areaNameId",
        "agendaDate",
        "agendaDateText",
        "lunarDate",
        "agendaSubjectZh",
        "agendaSubjectId",
        "rundownSectionTitleZh",
        "rundownSectionTitleId",
        "personnelSectionTitleZh",
        "personnelSectionTitleId",
        "financeSectionTitleZh",
        "financeSectionTitleId",
        "reportTemplateId"
      ];

      // 🔥 reload agenda + area
      const resp = await apiFetch(
        `/api/agendas/${agendaId}?populate=area`
      );

      const agendaData = resp?.data || resp;

      // 🔥 rebuild
      const newAttrs = await buildInitialAgendaAttributes(
        agendaData
      );

      const merged = {
        ...(agendaBook?.agendaAttributes || {})
      };

      // hanya overwrite system fields
      systemFields.forEach(k=>{
        merged[k] = newAttrs[k];
      });

      // 🔥 save
      await apiFetch(`/api/agenda-books/${agendaBook.documentId}`, {
        method:"PUT",
        body:{
          data:{
            agendaAttributes: merged
          }
        }
      });

      alert("Attributes berhasil di-rebuild");

      onClose(true); // trigger reload parent

    }catch(e){
      console.error(e);
      alert("Rebuild gagal");
    }
  }

  function renderRow(key, zh, id, disabled=false) {
    return (
      <div className="aa-row">
        <input
          type="checkbox"
          checked={attrs[key]}
          disabled={disabled}
          onChange={e => setAttrs({ ...attrs, [key]: e.target.checked })}
        />
        <div className="aa-label">
          <div className="aa-label-zh">{zh}</div>
          <div className="aa-label-id">{id}</div>
        </div>
      </div>
    );
  }

  if (!open) return null;

  const tabs = [
    { key: "attendance", label: isZh() ? "出席報表" : "Laporan Kehadiran" },
    { key: "registration", label: isZh() ? "報名設定" : "Pengaturan Pendaftaran" },
    { key: "reportFormat", label: isZh() ? "報表格式" : "Format Laporan" }
  ];

  return (
    <div className="aa-modal-overlay">

      {/* ===== STYLE ISOLATION ===== */}
      <style>{`
        .aa-modal-overlay { 
          position: fixed;
          inset: 0;
          background: rgba(0,0,0,0.35);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 999;
        }

        /* 🔥 container utama */
        .aa-modal {
          width: 520px;
          max-width: 95%;
          background: #fff;
          border-radius: 0.75rem;
          font-size: 0.95rem;

          display: flex;              
          flex-direction: column;     
          max-height: 85vh;           
        }

        /* 🔥 header fixed */
        .aa-header {
          flex-shrink: 0;
          padding: 1.5rem 1.5rem 0 1.5rem;
        }

        /* 🔥 body scroll */
        .aa-body {
          flex: 1;
          overflow-y: auto;
          padding: 0.5rem 1.5rem;
        }

        /* 🔥 footer fixed */
        .aa-actions {
          flex-shrink: 0;
          padding: 1rem 1.5rem 1.5rem 1.5rem;

          display: flex;
          justify-content: flex-end;
          gap: 0.5rem;
        }

        /* ===== existing styles tetap ===== */

        .aa-title {
          font-size: 1.2rem;
          font-weight: 700;
          margin-bottom: 0.75rem;
        }

        .aa-section {
          margin-top: 1rem;
        }

        .aa-row {
          display: flex;
          align-items: center;
          gap: 0.6rem;
          padding: 0.35rem 0;
        }

        .aa-row input {
          width: 1rem;
          height: 1rem;
        }

        .aa-label {
          display: flex;
          flex-direction: column;
          line-height: 1.2;
        }

        .aa-label-zh {
          font-weight: 600;
          color: #0f172a;
        }

        .aa-label-id {
          font-size: 0.8rem;
          color: #64748b;
        }

        .aa-select-row {
          margin-top: 0.75rem;
        }

        .aa-select-row select {
          width: 100%;
          padding: 0.45rem;
          border-radius: 0.5rem;
          border: 1px solid #dbe3ef;
        }
      `}</style>

      <div className="aa-modal">

        <div className="aa-header">
          <div className="aa-title">
            {isZh() ? "屬性設定" : "Konfigurasi Agenda"}
          </div>

          <TopNavTabs
            items={tabs}
            activeKey={activeTab}
            onTabClick={setActiveTab}
          />
        </div>

        <div className="aa-body">
          {/* ================= ATTENDANCE ================= */}
          {activeTab === "attendance" && (
            <div className="aa-section">

              {/* ===== BASIC OPTIONAL ===== */}
              {renderRow("includeArea", "地區", "Area")}
              {renderRow("includePhone", "電話", "Telepon")}
              {renderRow("includeAddress", "地址", "Alamat")}
              {renderRow("includeDutyLevel", "天職", "Jabatan Suci")}
              {renderRow("includeFotang", "所屬佛堂", "Fotang")}
              {renderRow("includeEducation", "學歷", "Pendidikan")}
              {renderRow("includeOccupation", "職業", "Pekerjaan")}

              {/* ===== UMATDATA_FULL (🔵) ===== */}
              {renderRow("includeBirthdate", "出生日期", "Tanggal Lahir", !canReadUmatDataFull)}
              {renderRow("includeQiudaoDate", "求道日期", "Tanggal Qiudao", !canReadUmatDataFull)}
              {renderRow("includeYinshi", "引師", "Yinshi", !canReadUmatDataFull)}
              {renderRow("includeBaoshi", "保師", "Baoshi", !canReadUmatDataFull)}
              {renderRow("includeFotangQiudao", "求道佛堂", "Fotang Qiudao", !canReadUmatDataFull)}
              {renderRow("includeQingkou", "清口", "QingKou", !canReadUmatDataFull)}
              {renderRow("includeSd3hari", "三天班", "SD 3 Hari", !canReadUmatDataFull)}
              {renderRow("includeMingde", "明德", "Ming De", !canReadUmatDataFull)}
              {renderRow("includeXinmin", "新民", "Xin Min", !canReadUmatDataFull)}
              {renderRow("includeZhishan", "至善", "Zhi Shan", !canReadUmatDataFull)}

              {/* ===== SPLIT ===== */}
              <div className="aa-select-row">
                <div className="aa-label-zh">匯出分組方式</div>
                <div className="aa-label-id">Pembagian Laporan</div>
                <select
                  value={attrs.exportSplitOption}
                  onChange={e => setAttrs({ ...attrs, exportSplitOption: e.target.value })}
                >
                  <option value="role_gender">Role + Gender (4 sheets)</option>
                  <option value="role">Role only (2 sheets)</option>
                </select>
              </div>

            </div>
          )}

          {/* ================= REGISTRATION ================= */}
          {activeTab === "registration" && (
            <div className="aa-section">

              <div className="aa-row">
                <input type="checkbox"
                  checked={reg.allowPreRegistration}
                  onChange={e => setReg({ ...reg, allowPreRegistration: e.target.checked })}
                />
                <div className="aa-label">
                  <div className="aa-label-zh">允許預先報名</div>
                  <div className="aa-label-id">Form Pra-Registrasi</div>
                </div>
              </div>

              <div className="aa-row">
                <input type="checkbox"
                  checked={reg.allowUmatExternal}
                  onChange={e => setReg({ ...reg, allowUmatExternal: e.target.checked })}
                />
                <div className="aa-label">
                  <div className="aa-label-zh">別的單位道親可報名</div>
                  <div className="aa-label-id">Umat Fotang Lain bisa daftar & hadir</div>
                </div>
              </div>

              <div className="aa-row">
                <input type="checkbox"
                  checked={reg.allowPublic}
                  onChange={e => setReg({ ...reg, allowPublic: e.target.checked })}
                />
                <div className="aa-label">
                  <div className="aa-label-zh">允許公開報名</div>
                  <div className="aa-label-id">Publik bisa daftar & hadir</div>
                </div>
              </div>

              <div className="aa-row">
                <input type="checkbox"
                  checked={reg.FaHui}
                  onChange={e => setReg({ ...reg, FaHui: e.target.checked })}
                />
                <div className="aa-label">
                  <div className="aa-label-zh">使用號碼牌</div>
                  <div className="aa-label-id">Pakai Nomor Kursi/Pendaftaran (Fa Hui)</div>
                </div>
              </div>

              <div className="aa-row">
                <input type="checkbox"
                  checked={reg.oneTimeTagNumber}
                  onChange={e => setReg({ ...reg, oneTimeTagNumber: e.target.checked })}
                />
                <div className="aa-label">
                  <div className="aa-label-zh">僅第一天使用號碼牌</div>
                  <div className="aa-label-id">Penomoran di hari pertama (Fa Hui)</div>
                </div>
              </div>

            </div>
          )}

          {activeTab === "reportFormat" && (
            <>
            <div className="aa-section">

              {[
                ["lunarDate", "農曆日期", "Tanggal Lunar"],
                ["agendaDate", "日期", "Tanggal Agenda"],
                ["agendaDateText", "日期文字", "Tanggal (Teks)"],
                ["areaNameId", "區域(印尼)", "Nama Area (ID)"],
                ["areaNameZh", "區域(中文)", "Nama Area (ZH)"],
                ["agendaSubjectId", "主題(印尼)", "Subjek (ID)"],
                ["agendaSubjectZh", "主題(中文)", "Subjek (ZH)"],
                ["reportTemplateId", "模板ID", "Template ID"],
                ["financeSectionTitleId", "收支表(印尼)", "Judul Keuangan (ID)"],
                ["financeSectionTitleZh", "收支表(中文)", "Judul Keuangan (ZH)"],
                ["rundownSectionTitleId", "流程表(印尼)", "Judul Rundown (ID)"],
                ["rundownSectionTitleZh", "流程表(中文)", "Judul Rundown (ZH)"],
                ["personnelSectionTitleId", "辦事人員表(印尼)", "Judul Petugas (ID)"],
                ["personnelSectionTitleZh", "辦事人員表(中文)", "Judul Petugas (ZH)"]
              ].map(([key, zh, id]) => (
                <div className="aa-row" key={key} style={{flexDirection:"column", alignItems:"flex-start"}}>
                  
                  <div className="aa-label">
                    <div className="aa-label-zh">{zh}</div>
                    <div className="aa-label-id">{id}</div>
                  </div>

                  <input
                    type="text"
                    value={report[key]}
                    onChange={e => setReport({ ...report, [key]: e.target.value })}
                    style={{
                      width: "100%",
                      padding: "0.45rem",
                      borderRadius: "0.5rem",
                      border: "1px solid #dbe3ef"
                    }}
                  />
                </div>
              ))}

            </div>
            </>
          )}
        </div>

        {activeTab === "reportFormat" && (
          <div style={{marginBottom:"1rem", margin: "10px 25px"}}>

            <button
              className="btn btn-secondary"
              onClick={handleRebuildAttributes}
              style={{width:"90%"}}
            >
              🔄 Rebuild Attributes (Auto)
            </button>

            <div style={{
              fontSize:"0.8rem",
              color:"#64748b",
              marginTop:"0.25rem"
            }}>
              Mengisi ulang data otomatis dari Agenda (area, tanggal, dll)
            </div>
          </div>            
        )}

        <div className="aa-actions">
          <button className="btn btn-secondary" onClick={()=>onClose(false)}>
            Batal
          </button>

          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? "Menyimpan..." : "Simpan"}
          </button>
        </div>

      </div>
    </div>
  );
}